import { Injectable } from '@angular/core';
import { SubscribeRedeemCodeParams, SubscribeRedeemCodeResult } from '@cf-shared/subscribe-redeem-interface';
import { readWorkoutSolverReply, WorkoutSolverParams, WorkoutSolverResults } from '@cf-shared/workout-solver-interface';
import { StoreGetProductsParams, StoreGetProductsResults } from '@cf-shared/store-interface';
import { PublishFoodParams, PublishFoodResults, UnpublishFoodParams, UnpublishFoodResults } from '@cf-shared/publish-food-interface';
import { PublishExerciseParams, PublishExerciseResults, UnpublishExerciseParams, UnpublishExerciseResults, GetExerciseParams, GetExerciseResults, PublishExerciseToCoachChannelParams, UnpublishExerciseFromCoachChannelParams } from '@cf-shared/publish-exercise-interface';
import {
  GenerateActivationKeyParams,
  GenerateActivationKeyResults,
  UseActivationKeyParams,
  UseActivationKeyResults,
  PayProUnsubscribeResult,
  DownloadUserDbResult,
  DownloadUserDbParams,
  PayProUnsubscribeParams,
  PayProResubscribeParams,
  PayProResubscribeResult,
} from '@cf-shared/activation-codes-interface';
import { HttpClient } from '@angular/common/http';
import { CouchLoginParams, CouchLoginResult } from '@cf-shared/couch-auth';
import { deepClone } from '@cf-shared/deep-helpers';
import { FirebaseService } from './firebase.service';
import { SetUserConsentParams, SetUserConsentResults } from '@cf-shared/user-consent-interface';
import { SsoDiscourseParams, SsoDiscourseResults } from '@cf-shared/sso-discourse-interface';
import { FeedbackPayload, SendFeedbackResults } from '@cf-shared/send-feedback-interface';
import { AdminGetUserDetailsParams, AdminGetUserDetailsResult } from '@cf-shared/admin-user-detail-interface';
import { AdminChangeSubscriptionParams, AdminChangeSubscriptionResult } from '@cf-shared/admin-change-subscription-interface';
import { AdminChangeEmailParams, AdminChangeEmailResult } from '@cf-shared/admin-change-email-interface';
import { UserSubscriptionParams, UserSubscriptionResult } from '@cf-shared/user-subscription-interface';
import { SignInByLinkParams, SignInByLinkResults } from '@cf-shared/signin-by-link-interface';
import { FlushTelemetryParams, FlushTelemetryResults } from '@cf-shared/flush-telemetry-interface';
import { CheckUserExistsParams, CheckUserExistsResult } from '@cf-shared/check-user-exists-interface';
import {
  ReferralClaimParams, ReferralClaimResult,
  ReferralGetOrCreateCodeParams, ReferralGetOrCreateCodeResult,
  ReferralListFriendsParams, ReferralListFriendsResult,
  ReferralPeekInviteParams, ReferralPeekInviteResult,
  ReferralReportTrainingParams, ReferralReportTrainingResult,
} from '@cf-shared/referral-interface';
import { ResolveUserEmailsParams, ResolveUserEmailsResult } from '@cf-shared/resolve-user-emails-interface';
import { GetLocaleParams, GetLocaleResult } from '@cf-shared/get-locale-interface';
import { DeleteMyScanDataParams, DeleteMyScanDataResult } from '@cf-shared/delete-scan-data-interface';
import { StorageGetUploadUrlParams, StorageGetUploadUrlResult, StorageGetDownloadUrlParams, StorageGetDownloadUrlResult, StorageDeleteParams, StorageDeleteResult } from '@cf-shared/storage-interface';
import { GooglePlayValidateParams, GooglePlayValidateResult, GooglePlayCancelParams, GooglePlayCancelResult, GetSubscriptionInfoParams, GetSubscriptionInfoResult } from '@cf-shared/google-play-interface';
import { AppleValidateParams, AppleValidateResult } from '@cf-shared/apple-validation-interface';
import { ExchangeAppleAuthCodeParams, ExchangeAppleAuthCodeResult } from '@cf-shared/siwa-auth-interface';
import { CoachCreateClaimTokenParams, CoachCreateClaimTokenResult, CoachValidateClaimTokenParams, CoachValidateClaimTokenResult, CoachClaimInviteParams, CoachClaimInviteResult, CoachDetachClientParams, CoachDetachClientResult, CoachSetClientSleepingParams, CoachSetClientSleepingResult, CoachClientSelfDetachParams, CoachClientSelfDetachResult, CoachUpdateBrandingParams, CoachUpdateBrandingResult, AdminSetCoachSlotsParams, AdminSetCoachSlotsResult } from '@cf-shared/coach-interface';
import { GetCoachBillingStateParams, GetCoachBillingStateResult, GetCoachTierCatalogParams, GetCoachTierCatalogResult, CoachTierValidateParams, CoachTierValidateResult, CoachStartPaddleCheckoutParams, CoachStartPaddleCheckoutResult } from '@cf-shared/coach-billing-interface';
import {
  AdminGetCoachBillingStateParams,
  AdminGetCoachBillingStateResult,
  AdminGetCoachHistoryParams,
  AdminGetCoachHistoryResult,
  AdminGetCoachInvoiceUrlParams,
  AdminGetCoachInvoiceUrlResult,
  GetCoachHistoryParams,
  GetCoachHistoryResult,
  GetCoachInvoiceUrlParams,
  GetCoachInvoiceUrlResult,
} from '@cf-shared/coach-history-interface';
import { PaddleManageSubscriptionParams, PaddleManageSubscriptionResult } from '@cf-shared/paddle-manage-interface';
import { ScanLabelParams, ScanLabelResults, LogLabelScanChoiceParams } from '@cf-shared/scan-label-interface';
import { ScanMealPhotoParams, ScanMealPhotoResults } from '@cf-shared/scan-meal-photo-interface';
import { ScanEquipmentParams, ScanEquipmentResults } from '@cf-shared/scan-equipment-interface';
import { ScanWorkoutPlanParams, ScanWorkoutPlanResults } from '@cf-shared/scan-workout-plan-interface';
import { ScanRecipeParams, ScanRecipeResults } from '@cf-shared/scan-recipe-interface';
import { ExchangePurchaseClaimTokenParams, ExchangePurchaseClaimTokenResult, GetPaddlePurchaseClaimTokenParams, GetPaddlePurchaseClaimTokenResult } from '@cf-shared/purchase-claim-interface';
import { ClientNotifyParams, CoachNotifyParams, CoachNotifyResult } from '@cf-shared/coach-notify-interface';
import { RemindersScheduleParams, RemindersScheduleResult, RemindersRegisterDeviceParams, RemindersRegisterDeviceResult, RemindersSuppressParams, RemindersSuppressResult, RemindersUnsuppressParams, RemindersUnsuppressResult } from '@cf-shared/reminders-interface';
import { ConnectionState, OnlineStateService } from './online-state.service';
import { LoggingService } from './logging.service';
import { isCapacitorNative } from 'environments/environment-helpers';
import { getApiOriginAsync } from 'util/backend-origin';
import { markCallerReportsFailure } from 'app/fetch-failure-classifier';
import { isPublicApiFunction } from '@cf-shared/api-functions';
import { createTimeoutSignal, isTimeoutError } from './timeout-signal';
import { ApiFetchWindow, RESILIENT_RETRY_DELAYS_MS, TerminalNetworkReport, decideTerminalNetworkReport, getRetryDelays, isRetriableHttpError, pickApiFetchImpl } from './cloud-functions-retry';
// The error classes + isNetworkError/isRetriableError live in this Angular-free leaf
// module (see its header comment) so a node-only consumer (couch-login-error-classify.ts)
// can import CloudFunctionHttpError/isNetworkError without pulling in @angular/core and
// this whole service's dependency chain. Re-exported here so every existing
// `from 'services/cloud-functions.service'` import of these keeps working unchanged.
export { CloudFunctionAuthError, CloudFunctionNetworkError, CloudFunctionHttpError, isNetworkError } from './cloud-functions-errors';
// `export ... from` above re-exports for OTHER files; it does not bring the names into
// this file's own scope, so the ones this file uses internally are imported separately.
import { CloudFunctionAuthError, CloudFunctionNetworkError, CloudFunctionHttpError, isRetriableError, markFetchFailureHandled, shouldReportUnrecognisedFetchFailure } from './cloud-functions-errors';

// the per-attempt deadline of a call that sets no `timeoutMs`; why 80s is explained at callApiAsync
const DEFAULT_CALL_TIMEOUT_MS = 80_000;

@Injectable()
export class CloudFunctionsService {
  public couchLoginAsync: (dummy: CouchLoginParams) => Promise<CouchLoginResult> = null;

  public solveWorkoutsAsync: (params: WorkoutSolverParams) => Promise<WorkoutSolverResults> = null;
  public publishFoodAsync: (params: PublishFoodParams) => Promise<PublishFoodResults> = null;
  public unpublishFoodAsync: (params: UnpublishFoodParams) => Promise<UnpublishFoodResults> = null;
  public scanLabelAsync: (params: ScanLabelParams) => Promise<ScanLabelResults> = null;
  public logLabelScanChoiceAsync: (params: LogLabelScanChoiceParams) => Promise<{ ok: boolean }> = null;
  public scanMealPhotoAsync: (params: ScanMealPhotoParams) => Promise<ScanMealPhotoResults> = null;
  public scanEquipmentAsync: (params: ScanEquipmentParams) => Promise<ScanEquipmentResults> = null;
  public scanWorkoutPlanAsync: (params: ScanWorkoutPlanParams) => Promise<ScanWorkoutPlanResults> = null;
  public scanRecipeAsync: (params: ScanRecipeParams) => Promise<ScanRecipeResults> = null;
  public generateActivationKeyAsync: (params: GenerateActivationKeyParams) => Promise<GenerateActivationKeyResults> = null;
  public useActivationKeyAsync: (params: UseActivationKeyParams) => Promise<UseActivationKeyResults> = null;
  public setUserConsent: (params: SetUserConsentParams) => Promise<SetUserConsentResults> = null;
  public payProUnsubscribeAsync: (params: PayProUnsubscribeParams) => Promise<PayProUnsubscribeResult> = null;
  public payProResubscribeAsync: (params: PayProResubscribeParams) => Promise<PayProResubscribeResult> = null;
  public adminGetUserDetailsAsync: (params: AdminGetUserDetailsParams) => Promise<AdminGetUserDetailsResult> = null;
  public adminChangeSubscriptionAsync: (params: AdminChangeSubscriptionParams) => Promise<AdminChangeSubscriptionResult> = null;
  public adminChangeEmailAsync: (params: AdminChangeEmailParams) => Promise<AdminChangeEmailResult> = null;
  public userSubscriptionAsync: (params: UserSubscriptionParams) => Promise<UserSubscriptionResult> = null;
  public downloadUserDbAsync: (params: DownloadUserDbParams) => Promise<DownloadUserDbResult> = null;
  public storeGetProducts_PayPro: (params: StoreGetProductsParams) => Promise<StoreGetProductsResults> = null;
  // provider-agnostic checkout-build dispatcher (#3861): decides PayPro vs Paddle
  // server-side via selectWebProvider and returns the matching checkout config.
  public storeGetCheckout: (params: StoreGetProductsParams) => Promise<StoreGetProductsResults> = null;
  public ssoDiscourse: (params: SsoDiscourseParams) => Promise<SsoDiscourseResults> = null;
  public sendFeedbackAsync: (params: FeedbackPayload) => Promise<SendFeedbackResults> = null;
  public flushTelemetry: (params: FlushTelemetryParams) => Promise<FlushTelemetryResults> = null;
  public signInByLink: (params: SignInByLinkParams) => Promise<SignInByLinkResults> = null;
  public checkUserExistsAsync: (params: CheckUserExistsParams) => Promise<CheckUserExistsResult> = null;
  public referralGetOrCreateCodeAsync: (params: ReferralGetOrCreateCodeParams) => Promise<ReferralGetOrCreateCodeResult> = null;
  public referralPeekInviteAsync: (params: ReferralPeekInviteParams) => Promise<ReferralPeekInviteResult> = null;
  public referralClaimAsync: (params: ReferralClaimParams) => Promise<ReferralClaimResult> = null;
  public subscribeRedeemCodeAsync: (params: SubscribeRedeemCodeParams) => Promise<SubscribeRedeemCodeResult> = null;
  public referralReportTrainingAsync: (params: ReferralReportTrainingParams) => Promise<ReferralReportTrainingResult> = null;
  public referralListFriendsAsync: (params: ReferralListFriendsParams) => Promise<ReferralListFriendsResult> = null;
  public resolveUserEmailsAsync: (params: ResolveUserEmailsParams) => Promise<ResolveUserEmailsResult> = null;
  public getLocaleAsync: (params: GetLocaleParams) => Promise<GetLocaleResult> = null;
  public storageGetUploadUrlAsync: (params: StorageGetUploadUrlParams) => Promise<StorageGetUploadUrlResult> = null;
  public storageGetDownloadUrlAsync: (params: StorageGetDownloadUrlParams) => Promise<StorageGetDownloadUrlResult> = null;
  public storageDeleteAsync: (params: StorageDeleteParams) => Promise<StorageDeleteResult> = null;
  public deleteMyScanDataAsync: (params: DeleteMyScanDataParams) => Promise<DeleteMyScanDataResult> = null;
  public googlePlayValidateAsync: (params: GooglePlayValidateParams) => Promise<GooglePlayValidateResult> = null;
  public googlePlayCancelAsync: (params: GooglePlayCancelParams) => Promise<GooglePlayCancelResult> = null;
  public appleValidateAsync: (params: AppleValidateParams) => Promise<AppleValidateResult> = null;
  public exchangeAppleAuthCodeAsync: (params: ExchangeAppleAuthCodeParams) => Promise<ExchangeAppleAuthCodeResult> = null;
  public getSubscriptionInfoAsync: (params: GetSubscriptionInfoParams) => Promise<GetSubscriptionInfoResult> = null;
  public exchangePurchaseClaimTokenAsync: (params: ExchangePurchaseClaimTokenParams) => Promise<ExchangePurchaseClaimTokenResult> = null;
  public getPaddlePurchaseClaimTokenAsync: (params: GetPaddlePurchaseClaimTokenParams) => Promise<GetPaddlePurchaseClaimTokenResult> = null;
  public coachCreateClaimTokenAsync: (params: CoachCreateClaimTokenParams) => Promise<CoachCreateClaimTokenResult> = null;
  public coachValidateClaimTokenAsync: (params: CoachValidateClaimTokenParams) => Promise<CoachValidateClaimTokenResult> = null;
  public coachClaimInviteAsync: (params: CoachClaimInviteParams) => Promise<CoachClaimInviteResult> = null;
  public coachDetachClientAsync: (params: CoachDetachClientParams) => Promise<CoachDetachClientResult> = null;
  public coachSetClientSleepingAsync: (params: CoachSetClientSleepingParams) => Promise<CoachSetClientSleepingResult> = null;
  public coachUpdateBrandingAsync: (params: CoachUpdateBrandingParams) => Promise<CoachUpdateBrandingResult> = null;
  public coachClientSelfDetachAsync: (params: CoachClientSelfDetachParams) => Promise<CoachClientSelfDetachResult> = null;
  public coachNotifyAsync: (params: CoachNotifyParams) => Promise<CoachNotifyResult> = null;
  public coachNotifyClientAsync: (params: ClientNotifyParams) => Promise<CoachNotifyResult> = null;
  public adminSetCoachSlotsAsync: (params: AdminSetCoachSlotsParams) => Promise<AdminSetCoachSlotsResult> = null;
  public getCoachBillingStateAsync: (params: GetCoachBillingStateParams) => Promise<GetCoachBillingStateResult> = null;
  public getCoachTierCatalogAsync: (params: GetCoachTierCatalogParams) => Promise<GetCoachTierCatalogResult> = null;
  public getCoachHistoryAsync: (params: GetCoachHistoryParams) => Promise<GetCoachHistoryResult> = null;
  public getCoachInvoiceUrlAsync: (params: GetCoachInvoiceUrlParams) => Promise<GetCoachInvoiceUrlResult> = null;
  public adminGetCoachHistoryAsync: (params: AdminGetCoachHistoryParams) => Promise<AdminGetCoachHistoryResult> = null;
  public adminGetCoachInvoiceUrlAsync: (params: AdminGetCoachInvoiceUrlParams) => Promise<AdminGetCoachInvoiceUrlResult> = null;
  public adminGetCoachBillingStateAsync: (params: AdminGetCoachBillingStateParams) => Promise<AdminGetCoachBillingStateResult> = null;
  public coachTierValidateAsync: (params: CoachTierValidateParams) => Promise<CoachTierValidateResult> = null;
  public coachStartPaddleCheckoutAsync: (params: CoachStartPaddleCheckoutParams) => Promise<CoachStartPaddleCheckoutResult> = null;
  public paddleManageSubscriptionAsync: (params: PaddleManageSubscriptionParams) => Promise<PaddleManageSubscriptionResult> = null;
  public publishExerciseAsync: (params: PublishExerciseParams) => Promise<PublishExerciseResults> = null;
  public unpublishExerciseAsync: (params: UnpublishExerciseParams) => Promise<UnpublishExerciseResults> = null;
  public publishExerciseToCoachChannelAsync: (params: PublishExerciseToCoachChannelParams) => Promise<PublishExerciseResults> = null;
  public unpublishExerciseFromCoachChannelAsync: (params: UnpublishExerciseFromCoachChannelParams) => Promise<UnpublishExerciseResults> = null;
  public getExerciseAsync: (params: GetExerciseParams) => Promise<GetExerciseResults> = null;
  public remindersScheduleAsync: (params: RemindersScheduleParams) => Promise<RemindersScheduleResult> = null;
  public remindersRegisterDeviceAsync: (params: RemindersRegisterDeviceParams) => Promise<RemindersRegisterDeviceResult> = null;
  public remindersSuppressAsync: (params: RemindersSuppressParams) => Promise<RemindersSuppressResult> = null;
  public remindersUnsuppressAsync: (params: RemindersUnsuppressParams) => Promise<RemindersUnsuppressResult> = null;

  constructor(
    public http: HttpClient,
    private firebase: FirebaseService,
    private onlineState: OnlineStateService,
    private logging: LoggingService,
  ) {
    void this.initAsync();
  }

  // Default `timeoutMs` of 80_000 = the 60s default per-function budget (DEFAULT_TIMEOUT_SECONDS
  // in server/functions/src/helpers/server-function-helpers.ts) + 20s headroom so the implicit
  // pairing is correctly ordered for any function that keeps the default budget. (The Cloud Run
  // service-level cap is `--timeout=600` in server/deploy.js; the per-function budget is the
  // binding constraint.) Pre-#3570 the default was 30_000, the #3366 anti-pattern. Callers can
  // override `timeoutMs`.
  //
  // `webFetchOnNative`: on Capacitor native platforms, run this request through the browser's
  // original fetch instead of the CapacitorHttp-patched one — see pickApiFetchImpl (#4038).
  private async callApiAsync<T>(name: string, params: any, authRequired: boolean, timeoutMs = DEFAULT_CALL_TIMEOUT_MS, webFetchOnNative = false): Promise<T> {
    // #4280/#4405: this is the route-SENSITIVE kind of call — couchLogin and
    // every other declared function runs real server logic, so reaching the
    // wrong server is the #4273 lockout class (a build newer than live, pinned
    // to live, whose older code rejected its credentials). getApiOriginAsync()
    // below therefore waits for the backend route rather than guessing a host.
    // On every boot but the first-ever one the route is already known and the
    // await costs nothing; #4405 removed the native boot hold that used to make
    // that true by force. See design-docs/backend-routing.md.
    // During warm-resume Phase A, AuthService synthesizes a cached login but
    // skips awaiting authStateReady() (#3184: that probe can hang ~10s on Android).
    // fbAuth.currentUser stays null until Firebase's listener fires asynchronously.
    // Race authStateReady() against a short timeout here so authenticated callers
    // (e.g. a share-link arrival on the read-only exercise page → getExercise) don't
    // fail mid-warm-resume — it resolves immediately once auth has settled in either
    // path. The 5s ceiling prevents a transient-DNS Android probe from blocking
    // user-visible cloud calls indefinitely; on timeout we fall through to the
    // original fast-fail semantics (CloudFunctionAuthError below).
    if (authRequired) {
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          this.firebase.fbAuth.authStateReady(),
          new Promise<void>(resolve => { timeoutHandle = setTimeout(resolve, 5000); }),
        ]);
      } finally {
        if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      }
    }
    const user = this.firebase.fbAuth.currentUser;
    const token = user ? await user.getIdToken() : null;
    if (authRequired && !token) {
      throw new CloudFunctionAuthError(name);
    }
    const apiFetchWindow = window as unknown as ApiFetchWindow;
    const isNative = isCapacitorNative();
    // if a native webFetchOnNative call cannot get the saved browser fetch, it
    // silently falls back to the CapacitorHttp-patched fetch — which carries
    // the ~60s iOS cap this flag exists to escape. On web that is the normal
    // path, but on native it means the bridge stopped saving the original
    // (e.g. a Capacitor upgrade); report it so a #4038 regression is visible.
    if (webFetchOnNative && isNative && typeof apiFetchWindow.CapacitorWebFetch !== 'function') {
      this.logging.trackSilentProblem(
        `Cloud function "${name}" webFetchOnNative fallback: CapacitorWebFetch missing on native`,
        { name },
      );
    }
    const fetchImpl = pickApiFetchImpl(webFetchOnNative, isNative, apiFetchWindow);
    const apiOrigin = await getApiOriginAsync();
    // marked so the global fetch wrapper leaves a failure to callable(), which retries it and
    // files the one report if the retries run out too (#6058)
    const url = `${apiOrigin}/api/${name}`;
    let response: Response;
    try {
      response = await fetchImpl(url, markCallerReportsFailure({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(params),
        signal: createTimeoutSignal(timeoutMs),
      }));
    } catch (fetchError) {
      // if callable() will not recognise this rejection as a network failure (e.g. a native
      // CapacitorHttp "Could not connect to the server."), it rethrows it without retrying or
      // reporting -- and the mark keeps the wrapper quiet -- so it is reported here instead,
      // after the same online check the wrapper used
      if (shouldReportUnrecognisedFetchFailure(fetchError, url)) {
        await this.reportIfOnlineAsync(`Cloud function "${name}" fetch failed`, { error: fetchError });
        markFetchFailureHandled(fetchError);
      }
      throw fetchError;
    }
    if (!response.ok) {
      throw new CloudFunctionHttpError(name, response.status);
    }
    return response.json();
  }

  /** Files a silent problem unless the device turns out to be offline, in which case the failure
   *  is expected and only logged. */
  private async reportIfOnlineAsync(problemName: string, context: Record<string, unknown>) {
    const actualState = await this.onlineState.verifyOnlineStatus(ConnectionState.Offline, 'suspected');
    if (actualState === ConnectionState.Offline) {
      console.log(`${problemName} while offline`, context);
      return;
    }
    this.logging.trackSilentProblem(problemName, context);
  }

  async initAsync() {

    await this.firebase.initialized;

    let callable = <inT, outT>(name: string, options?: { resilientRetry?: boolean, maxRetries?: number, timeoutMs?: number, silentNetworkErrors?: boolean, webFetchOnNative?: boolean }): ((params: inT) => Promise<outT>) => {
      const authRequired = !isPublicApiFunction(name);
      const resilient = options?.resilientRetry ?? false;
      const maxRetries = options?.maxRetries;
      const timeoutMs = options?.timeoutMs;
      const silentNetworkErrors = options?.silentNetworkErrors ?? false;
      const webFetchOnNative = options?.webFetchOnNative ?? false;

      return async (params: inT) => {
        // deepClone removes `undefined` members which could break server-side comparisons
        if (typeof params === 'object') {
          params = deepClone(params);
        }

        // attempts counts every call to callApiAsync, including the initial one.
        // Reported to Sentry on terminal failure so we can distinguish retry-exhaustion
        // events on `maxRetries`-capped callables (#3575) from single-fail events.
        let attempts = 1;
        // lastError tracks the most-recent failure so the terminal branch can
        // discriminate on what actually failed last, not what failed first. A
        // mixed sequence (e.g. network error → 503, or 503 → network error)
        // would otherwise pick the wrong terminal route and lose the actual
        // last error's payload / type to the caller.
        let lastError: unknown;
        // set when the app's own connection state reads Offline before or after any attempt, so the
        // terminal report does not claim "while online" for a call that failed during a reconnect
        // (#6609, see decideTerminalNetworkReport)
        let wasOfflineDuringAttempts = false;
        // set when the page is hidden at any point during the call. Android can cut a background
        // app's network while the app's own connection state stays Online, and the delayed retry may
        // run after the page is visible again, so a listener catches a hide between the notes
        // (df-50916fe9c89e, see decideTerminalNetworkReport)
        let wasHiddenDuringAttempts = document.hidden;
        const noteHidden = () => {
          if (document.hidden) wasHiddenDuringAttempts = true;
        };
        document.addEventListener('visibilitychange', noteHidden);
        const noteConnectionState = () => {
          if (!this.onlineState.isConnectionStateOnline()) wasOfflineDuringAttempts = true;
          noteHidden();
        };
        // run one attempt, noting the connection state before it and after a failure. every
        // attempt below (the first, the resilient retries and the single retry) goes through here
        const attemptAsync = async () => {
          noteConnectionState();
          try {
            return await this.callApiAsync<outT>(name, params, authRequired, timeoutMs, webFetchOnNative);
          } catch (attemptError) {
            noteConnectionState();
            throw attemptError;
          }
        };
        try {
          return await attemptAsync();
        } catch (error) {
          lastError = error;
          // Retry on network errors OR on retriable 5xx (502/503 — Bug 11, see
          // isRetriableHttpError). Any other error (5xx that isn't 502/503,
          // CloudFunctionAuthError, JSON parse) surfaces to caller unmodified.
          if (!isRetriableError(error)) throw error;

          if (resilient) {
            const delays = getRetryDelays(maxRetries);
            for (let i = 0; i < delays.length; i++) {
              console.log(`Cloud function "${name}" retriable error, retry ${i + 1}/${delays.length} in ${delays[i] / 1000}s...`);
              await new Promise(r => setTimeout(r, delays[i]));
              attempts++;
              try {
                return await attemptAsync();
              } catch (retryError) {
                lastError = retryError;
                if (!isRetriableError(retryError)) throw retryError;
              }
            }
            console.error(`Cloud function "${name}" failed after ${delays.length} retries`);
          } else if (!isTimeoutError(error)) {
            // single retry with 3s delay (skip for timeouts — retrying with same timeout won't help)
            console.log(`Cloud function "${name}" failed with retriable error, retrying in 3s...`);
            await new Promise(r => setTimeout(r, 3000));
            attempts++;
            try {
              return await attemptAsync();
            } catch (retryError) {
              lastError = retryError;
              // mirror the resilient branch: non-retriable errors on retry (HTTP 500,
              // CloudFunctionAuthError, JSON parse) must surface to the caller, not be absorbed.
              if (!isRetriableError(retryError)) throw retryError;
              console.error(`Cloud function "${name}" retry also failed:`, retryError);
            }
          }

          // terminal failure path. 5xx and network errors take different routes.
          // 5xx exhaustion: the last attempt got an HTTP response, so we're
          // demonstrably online — skip the verifyOnlineStatus probe (it would
          // be a slow no-op) and rethrow the original CloudFunctionHttpError
          // instead of wrapping in CloudFunctionNetworkError (callers need the
          // status code).
          if (isRetriableHttpError(lastError)) {
            if (!silentNetworkErrors) {
              const retryBudget = resilient ? (maxRetries ?? RESILIENT_RETRY_DELAYS_MS.length) : 1;
              this.logging.trackSilentProblem(
                `Cloud function "${name}" HTTP ${(lastError as CloudFunctionHttpError).httpStatus} error after retries`,
                { error: lastError, attempts, retryBudget, resilient },
              );
            }
            throw lastError;
          }

          // network-error terminal failure: the global fetch wrapper leaves these requests to us
          // (#6058), so this is the one report. see decideTerminalNetworkReport for the cases
          // retryBudget reflects what this callable actually plans: a resilient callable's
          // schedule length (5 or the capped maxRetries), or 1 for the non-resilient single
          // retry. Reporting maxRetries: 5 for a non-resilient callable misleadingly suggests
          // a 6-attempt budget when the real cap is 2 attempts.
          const retryBudget = resilient ? (maxRetries ?? RESILIENT_RETRY_DELAYS_MS.length) : 1;
          switch (decideTerminalNetworkReport(lastError, silentNetworkErrors, wasOfflineDuringAttempts, wasHiddenDuringAttempts)) {
            case TerminalNetworkReport.None:
              break;
            case TerminalNetworkReport.LogOfflineDuringAttempts:
              console.log(
                `Cloud function "${name}" network error while the app was offline during the attempts`,
                { error: lastError, attempts, retryBudget, resilient },
              );
              break;
            case TerminalNetworkReport.LogHiddenDuringAttempts:
              console.log(
                `Cloud function "${name}" network error while the app was in the background during the attempts`,
                { error: lastError, attempts, retryBudget, resilient },
              );
              break;
            case TerminalNetworkReport.ReportTimeout:
              await this.reportIfOnlineAsync(
                `Cloud function "${name}" timed out`,
                { error: lastError, attempts, retryBudget, resilient, timeoutMs: timeoutMs ?? DEFAULT_CALL_TIMEOUT_MS },
              );
              break;
            case TerminalNetworkReport.ReportNetworkError:
              await this.reportIfOnlineAsync(
                `Cloud function "${name}" network error while online`,
                { error: lastError, attempts, retryBudget, resilient },
              );
              break;
          }
          throw new CloudFunctionNetworkError(name, lastError);
        } finally {
          document.removeEventListener('visibilitychange', noteHidden);
        }
      };
    };

    // resilientRetry: exponential backoff up to ~30s for critical operations where
    // failure = data loss, broken state, or blocked user flow
    const resilient = { resilientRetry: true };

    // resilientCapped: same exponential backoff but stops after 3 retries (4 attempts total),
    // capping worst-case wall-clock at ~5.5 min instead of ~8.5 min (#3575). Applied to the 12
    // user-blocking callables whose UI shows a loader/spinner while awaiting the result —
    // payment cancel/resume, IAP validation, coach onboarding, post-checkout email, sync feedback.
    // Best-effort background callables keep `resilient` (5 retries, 8.5 min ceiling).
    const resilientCapped = { resilientRetry: true, maxRetries: 3 };

    this.couchLoginAsync = callable<CouchLoginParams, CouchLoginResult>('couchLogin', { timeoutMs: 5_000, silentNetworkErrors: true });

    // Server enforces timeoutSeconds: 180 in server/functions/src/functions/solveWorkouts.ts.
    // Client must outlast that — 200_000 ms = server 180s + ~20s headroom for
    // cold-start, TLS, body upload, JSON parse. If you change one side, change
    // the other (see #3366: a 30s default here vs the server's 180s caused
    // silent AbortError-on-fetch even when the server had a full payload ready).
    //
    // Deliberately NOT resilientRetry: this is a long, user-visible operation
    // gated by a loader the UI promises will take "a minute or two." The
    // resilient path retries on any network-class error with 1/2/4/8/15s
    // backoff, re-using this 200s timeout per attempt — worst case the user
    // waits ~17 min before the final failure surfaces, wildly off the UI copy.
    // The server's own 180s hard cap also means a server-timeout on attempt 1
    // would just repeat on every retry, so retrying buys nothing.
    //
    // webFetchOnNative (#4038): the CapacitorHttp-patched fetch ignores the
    // 200s AbortSignal above and, on iOS, is hard-capped at ~60s by
    // URLSession.shared's session-level timeout — which killed legitimately
    // slow (~85s) solves that the server then completed for nothing. The
    // browser's original fetch honors the 200s pairing on native. See
    // pickApiFetchImpl in cloud-functions-retry.ts for the full story.
    //
    // every reply is read through readWorkoutSolverReply (#5942), so a caller always gets an
    // `outcome` to check: a reply from a server older than that field is understood, and a
    // damaged one reads as a failure rather than as an empty program.
    const solveWorkoutsRawAsync = callable<WorkoutSolverParams, unknown>('solveWorkouts', { timeoutMs: 200_000, webFetchOnNative: true });
    this.solveWorkoutsAsync = async (params: WorkoutSolverParams) => readWorkoutSolverReply(await solveWorkoutsRawAsync(params));
    this.publishFoodAsync = callable<PublishFoodParams, PublishFoodResults>('publishFood', resilient);
    this.unpublishFoodAsync = callable<UnpublishFoodParams, UnpublishFoodResults>('unpublishFood', resilient);
    this.scanLabelAsync = callable<ScanLabelParams, ScanLabelResults>('scanLabel');
    this.logLabelScanChoiceAsync = callable<LogLabelScanChoiceParams, { ok: boolean }>('logLabelScanChoice');
    this.scanMealPhotoAsync = callable<ScanMealPhotoParams, ScanMealPhotoResults>('scanMealPhoto');
    this.scanEquipmentAsync = callable<ScanEquipmentParams, ScanEquipmentResults>('scanEquipment');
    this.scanWorkoutPlanAsync = callable<ScanWorkoutPlanParams, ScanWorkoutPlanResults>('scanWorkoutPlan');
    this.scanRecipeAsync = callable<ScanRecipeParams, ScanRecipeResults>('scanRecipe');
    this.generateActivationKeyAsync = callable<GenerateActivationKeyParams, GenerateActivationKeyResults>('generateActivationKey');
    this.useActivationKeyAsync = callable<UseActivationKeyParams, UseActivationKeyResults>('useActivationKey');
    this.setUserConsent = callable<SetUserConsentParams, SetUserConsentResults>('setUserConsent');
    this.payProUnsubscribeAsync = callable<PayProUnsubscribeParams, PayProUnsubscribeResult>('payProUnsubscribe', resilientCapped);
    this.payProResubscribeAsync = callable<PayProResubscribeParams, PayProResubscribeResult>('payProResubscribe', resilientCapped);
    this.adminGetUserDetailsAsync = callable<AdminGetUserDetailsParams, AdminGetUserDetailsResult>('adminGetUserDetails');
    this.adminChangeSubscriptionAsync = callable<AdminChangeSubscriptionParams, AdminChangeSubscriptionResult>('adminChangeSubscription');
    this.adminChangeEmailAsync = callable<AdminChangeEmailParams, AdminChangeEmailResult>('adminChangeEmail');
    this.userSubscriptionAsync = callable<UserSubscriptionParams, UserSubscriptionResult>('userSubscription', resilientCapped);
    this.downloadUserDbAsync = callable<DownloadUserDbParams, DownloadUserDbResult>('downloadUserDb');
    this.storeGetProducts_PayPro = callable<StoreGetProductsParams, StoreGetProductsResults>('storeGetProducts_PayPro');
    this.storeGetCheckout = callable<StoreGetProductsParams, StoreGetProductsResults>('storeGetCheckout');
    this.ssoDiscourse = callable<SsoDiscourseParams, SsoDiscourseResults>('ssoDiscourse');
    this.sendFeedbackAsync = callable<FeedbackPayload, SendFeedbackResults>('sendFeedback', resilientCapped);
    // Server enforces timeoutSeconds: 60. Explicit `timeoutMs: 80_000` (= server 60s + 20s headroom)
    // matches the implicit callApiAsync default but is kept so the AST guard's `timeoutSeconds > 10`
    // lockstep-pairing rule holds (#3366 / #3562 / #3570).
    this.flushTelemetry = callable<FlushTelemetryParams, FlushTelemetryResults>('flushTelemetry', { timeoutMs: 80_000, silentNetworkErrors: true });
    this.signInByLink = callable<SignInByLinkParams, SignInByLinkResults>('signInByLink');
    this.checkUserExistsAsync = callable<CheckUserExistsParams, CheckUserExistsResult>('checkUserExists', resilientCapped);

    // The referral programme (#4627, extended by #4963). Most take the capped
    // resilient policy: the authenticated ones are things a person just tapped and
    // is watching a spinner for, and the public one is a stranger's very first
    // impression of us, on a page that can say nothing at all until it answers.
    // See design-docs/referral-program.md.
    this.referralGetOrCreateCodeAsync = callable<ReferralGetOrCreateCodeParams, ReferralGetOrCreateCodeResult>('referralGetOrCreateCode', resilientCapped);
    this.referralPeekInviteAsync = callable<ReferralPeekInviteParams, ReferralPeekInviteResult>('referralPeekInvite', resilientCapped);
    this.referralClaimAsync = callable<ReferralClaimParams, ReferralClaimResult>('referralClaim', resilientCapped);
    // safe to repeat: a repeat friend claim writes nothing, and a lost access redemption answers AccessRedeemed (#6984)
    this.subscribeRedeemCodeAsync = callable<SubscribeRedeemCodeParams, SubscribeRedeemCodeResult>('subscribeRedeemCode', resilientCapped);
    // The one exception, and deliberately so: nobody is watching this call. It
    // fires behind the workout-completion card, and if it fails the next completed
    // workout tries again — so retrying hard here would spend a person's battery
    // and connection on something that already has a better retry than any policy.
    this.referralReportTrainingAsync = callable<ReferralReportTrainingParams, ReferralReportTrainingResult>('referralReportTraining');
    this.referralListFriendsAsync = callable<ReferralListFriendsParams, ReferralListFriendsResult>('referralListFriends', resilientCapped);
    this.resolveUserEmailsAsync = callable<ResolveUserEmailsParams, ResolveUserEmailsResult>('resolveUserEmails');
    this.getLocaleAsync = callable<GetLocaleParams, GetLocaleResult>('getLocale');
    this.storageGetUploadUrlAsync = callable<StorageGetUploadUrlParams, StorageGetUploadUrlResult>('storageGetUploadUrl');
    this.storageGetDownloadUrlAsync = callable<StorageGetDownloadUrlParams, StorageGetDownloadUrlResult>('storageGetDownloadUrl');
    this.storageDeleteAsync = callable<StorageDeleteParams, StorageDeleteResult>('storageDelete');
    this.deleteMyScanDataAsync = callable<DeleteMyScanDataParams, DeleteMyScanDataResult>('deleteMyScanData');
    this.googlePlayValidateAsync = callable<GooglePlayValidateParams, GooglePlayValidateResult>('googlePlayValidate', resilientCapped);
    this.googlePlayCancelAsync = callable<GooglePlayCancelParams, GooglePlayCancelResult>('googlePlayCancel', resilientCapped);
    this.appleValidateAsync = callable<AppleValidateParams, AppleValidateResult>('appleValidate', resilientCapped);
    this.exchangeAppleAuthCodeAsync = callable<ExchangeAppleAuthCodeParams, ExchangeAppleAuthCodeResult>('exchangeAppleAuthCode');
    this.getSubscriptionInfoAsync = callable<GetSubscriptionInfoParams, GetSubscriptionInfoResult>('getSubscriptionInfo');
    this.exchangePurchaseClaimTokenAsync = callable<ExchangePurchaseClaimTokenParams, ExchangePurchaseClaimTokenResult>('exchangePurchaseClaimToken');
    this.getPaddlePurchaseClaimTokenAsync = callable<GetPaddlePurchaseClaimTokenParams, GetPaddlePurchaseClaimTokenResult>('getPaddlePurchaseClaimToken');
    this.coachCreateClaimTokenAsync = callable<CoachCreateClaimTokenParams, CoachCreateClaimTokenResult>('coachCreateClaimToken');
    this.coachValidateClaimTokenAsync = callable<CoachValidateClaimTokenParams, CoachValidateClaimTokenResult>('coachValidateClaimToken');
    this.coachClaimInviteAsync = callable<CoachClaimInviteParams, CoachClaimInviteResult>('coachClaimInvite', resilientCapped);
    this.coachDetachClientAsync = callable<CoachDetachClientParams, CoachDetachClientResult>('coachDetachClient', resilientCapped);
    // #4530: parking a client and waking them again. Same retry policy as
    // detaching, for the same reason — it is a coach-initiated write that
    // changes a client's access, so a transient network failure must be
    // retried rather than leaving the coach unsure whether it took effect.
    this.coachSetClientSleepingAsync = callable<CoachSetClientSleepingParams, CoachSetClientSleepingResult>('coachSetClientSleeping', resilientCapped);
    this.coachUpdateBrandingAsync = callable<CoachUpdateBrandingParams, CoachUpdateBrandingResult>('coachUpdateBranding', resilientCapped);
    this.coachClientSelfDetachAsync = callable<CoachClientSelfDetachParams, CoachClientSelfDetachResult>('coachClientSelfDetach', resilientCapped);
    this.coachNotifyAsync = callable<CoachNotifyParams, CoachNotifyResult>('coachNotify', resilient);
    this.coachNotifyClientAsync = callable<ClientNotifyParams, CoachNotifyResult>('coachNotifyClient', resilient);
    this.adminSetCoachSlotsAsync = callable<AdminSetCoachSlotsParams, AdminSetCoachSlotsResult>('adminSetCoachSlots');
    // user-blocking — the dashboard renders a loader/skeleton while awaiting
    // this, so it follows the #3575 resilientCapped convention (3 retries,
    // ~5.5 min worst-case ceiling) rather than the open-ended `resilient`
    // (5 retries, ~8.5 min) reserved for best-effort background callables.
    this.getCoachBillingStateAsync = callable<GetCoachBillingStateParams, GetCoachBillingStateResult>('getCoachBillingState', resilientCapped);
    this.getCoachTierCatalogAsync = callable<GetCoachTierCatalogParams, GetCoachTierCatalogResult>('getCoachTierCatalog', resilientCapped);
    // the coach billing page's history cards (#6768). The two invoice calls get NO retry: the
    // link lasts an hour and is fetched per tap, and on web a retry only lengthens the wait while
    // an empty window opened for it stays blank. With no options a callable still retries once
    // after 3 s, so retries are switched off explicitly.
    this.getCoachHistoryAsync = callable<GetCoachHistoryParams, GetCoachHistoryResult>('getCoachHistory', resilientCapped);
    this.adminGetCoachHistoryAsync = callable<AdminGetCoachHistoryParams, AdminGetCoachHistoryResult>('adminGetCoachHistory', resilientCapped);
    this.adminGetCoachBillingStateAsync = callable<AdminGetCoachBillingStateParams, AdminGetCoachBillingStateResult>('adminGetCoachBillingState', resilientCapped);
    this.getCoachInvoiceUrlAsync = callable<GetCoachInvoiceUrlParams, GetCoachInvoiceUrlResult>('getCoachInvoiceUrl', { resilientRetry: true, maxRetries: 0 });
    this.adminGetCoachInvoiceUrlAsync = callable<AdminGetCoachInvoiceUrlParams, AdminGetCoachInvoiceUrlResult>('adminGetCoachInvoiceUrl', { resilientRetry: true, maxRetries: 0 });
    this.coachTierValidateAsync = callable<CoachTierValidateParams, CoachTierValidateResult>('coachTierValidate', resilientCapped);
    this.coachStartPaddleCheckoutAsync = callable<CoachStartPaddleCheckoutParams, CoachStartPaddleCheckoutResult>('coachStartPaddleCheckout', resilientCapped);
    // user-blocking: the profile page shows a spinner while awaiting a
    // cancel / resume / portal call, so it follows the resilientCapped convention.
    this.paddleManageSubscriptionAsync = callable<PaddleManageSubscriptionParams, PaddleManageSubscriptionResult>('paddleManageSubscription', resilientCapped);
    this.publishExerciseAsync = callable<PublishExerciseParams, PublishExerciseResults>('publishExercise', resilient);
    this.unpublishExerciseAsync = callable<UnpublishExerciseParams, UnpublishExerciseResults>('unpublishExercise', resilient);
    this.publishExerciseToCoachChannelAsync = callable<PublishExerciseToCoachChannelParams, PublishExerciseResults>('publishExerciseToCoachChannel', resilientCapped);
    this.unpublishExerciseFromCoachChannelAsync = callable<UnpublishExerciseFromCoachChannelParams, UnpublishExerciseResults>('unpublishExerciseFromCoachChannel', resilient);
    this.getExerciseAsync = callable<GetExerciseParams, GetExerciseResults>('getExercise');
    this.remindersScheduleAsync = callable<RemindersScheduleParams, RemindersScheduleResult>('remindersSchedule');
    this.remindersRegisterDeviceAsync = callable<RemindersRegisterDeviceParams, RemindersRegisterDeviceResult>('remindersRegisterDevice');
    this.remindersSuppressAsync = callable<RemindersSuppressParams, RemindersSuppressResult>('remindersSuppress');
    this.remindersUnsuppressAsync = callable<RemindersUnsuppressParams, RemindersUnsuppressResult>('remindersUnsuppress');
  }
}
